import { Filter, X } from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { adminClient } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { DataTable, DataTableLoadError, DataTableMessage } from '@/components/table/DataTable';
import { SegmentedFilter } from '@/components/table/SegmentedFilter';
import { TablePager } from '@/components/table/TablePager';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatDateTime } from '@/lib/format';

import { pickOrg, readRememberedOrg } from '../Users/usersModel';
import { useAppNames } from './names';
import { type Check, type CheckQuery, errorText, permissionsApi } from './permissionsApi';
import { objectText } from './shared';

const PAGE_SIZE = 50;

type Period = 'any' | '1d' | '7d' | '30d';
type Choice = { id: string; label: string };
export type LogFilters = {
  /** Deny is the default: the log is for finding what was refused. */
  answer: 'deny' | 'allow' | 'all';
  /** The person id (`user:<id>`), with the email to show. */
  person?: Choice;
  /** An integration level, `slack.read`. */
  level?: Choice;
  period: Period;
  /** The three below filter the loaded page in the browser. */
  app: string;
  reason: string;
  missing: string;
};
export const DEFAULT_FILTERS: LogFilters = { answer: 'deny', period: 'any', app: '', reason: '', missing: '' };

const DAY_MS = 86_400_000;
const PERIOD_DAYS: Record<Exclude<Period, 'any'>, number> = { '1d': 1, '7d': 7, '30d': 30 };
const PERIOD_TEXT: Record<Period, string> = { any: 'Any time', '1d': 'Last 24 hours', '7d': 'Last 7 days', '30d': 'Last 30 days' };

/** The filters the service has, as its query. `now` is a parameter so a test can fix it. */
export function serviceQuery(filters: LogFilters, before: number | undefined, now = Date.now()): CheckQuery {
  return {
    ...(filters.person ? { subject: filters.person.id } : {}),
    ...(filters.level ? { resource: `integration_level:${filters.level.id}` } : {}),
    ...(filters.answer !== 'all' ? { answer: filters.answer } : {}),
    ...(filters.period !== 'any' ? { from: new Date(now - PERIOD_DAYS[filters.period] * DAY_MS).toISOString() } : {}),
    limit: PAGE_SIZE,
    ...(before ? { before } : {}),
  };
}

/**
 * The filters the service does not have yet (app, reason, missing part).
 * shortcut: filters the loaded page only, move to the service after the check_log index
 */
export function filterLoaded(checks: Check[], filters: Pick<LogFilters, 'app' | 'reason' | 'missing'>): Check[] {
  const has = (text: string | undefined | null, needle: string) => !needle.trim() || (text ?? '').toLowerCase().includes(needle.trim().toLowerCase());
  return checks.filter((c) => has(c.app, filters.app) && has(c.reason, filters.reason) && has((c.missing ?? []).join(' '), filters.missing));
}

/** The active filters as chips, each with the change that removes it. */
export function chipsOf(filters: LogFilters): Array<{ key: string; label: string; remove: Partial<LogFilters> }> {
  const chips: Array<{ key: string; label: string; remove: Partial<LogFilters> }> = [];
  if (filters.answer !== 'all') chips.push({ key: 'answer', label: `Answer: ${filters.answer === 'deny' ? 'Deny' : 'Allow'}`, remove: { answer: 'all' } });
  if (filters.person) chips.push({ key: 'person', label: `Person: ${filters.person.label}`, remove: { person: undefined } });
  if (filters.level) chips.push({ key: 'level', label: `Integration: ${filters.level.label}`, remove: { level: undefined } });
  if (filters.period !== 'any') chips.push({ key: 'period', label: PERIOD_TEXT[filters.period], remove: { period: 'any' } });
  if (filters.app.trim()) chips.push({ key: 'app', label: `App: ${filters.app.trim()}`, remove: { app: '' } });
  if (filters.reason.trim()) chips.push({ key: 'reason', label: `Reason: ${filters.reason.trim()}`, remove: { reason: '' } });
  if (filters.missing.trim()) chips.push({ key: 'missing', label: `Missing: ${filters.missing.trim()}`, remove: { missing: '' } });
  return chips;
}

export function LogTab() {
  const [filters, setFilters] = useState<LogFilters>(DEFAULT_FILTERS);
  const [popover, setPopover] = useState(false);
  const [paging, setPaging] = useState<{ key: string; cursors: Array<number | undefined> }>({ key: '', cursors: [undefined] });
  const [page, setPage] = useState<{ checks: Check[]; next: number | null }>();
  const [error, setError] = useState<string>();
  const [openRow, setOpenRow] = useState<number>();
  // The service filters in these keys. A change starts again from the newest page.
  const key = JSON.stringify([filters.answer, filters.person?.id, filters.level?.id, filters.period]);
  const cursors = paging.key === key ? paging.cursors : [undefined];
  const before = cursors[cursors.length - 1];

  const load = useCallback(async () => {
    setError(undefined);
    setPage(undefined);
    try {
      setPage(await permissionsApi.checks(serviceQuery(filters, before)));
    } catch (failure) {
      setError(errorText(failure));
    }
    // The browser filters (app, reason, missing) do not reload the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, before]);
  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => filterLoaded(page?.checks ?? [], filters), [page, filters]);
  const appName = useAppNames(rows.map((c) => c.app));
  const apply = (next: LogFilters) => {
    setFilters(next);
    setPopover(false);
  };
  const chips = chipsOf(filters);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Button type="button" variant="outline" size="sm" aria-haspopup="dialog" aria-expanded={popover} onClick={() => setPopover((open) => !open)}>
            <Filter className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            Filter
          </Button>
          {popover ? <FilterPopover filters={filters} onApply={apply} onClose={() => setPopover(false)} /> : null}
        </div>
        {chips.map((chip) => (
          <span key={chip.key} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 py-0.5 pl-2.5 pr-1 text-xs">
            {chip.label}
            <button type="button" aria-label={`Remove filter ${chip.label}`} onClick={() => setFilters({ ...filters, ...chip.remove })} className="rounded-full p-0.5 text-muted-foreground hover:text-foreground">
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          </span>
        ))}
      </div>
      <DataTable
        footer={
          page ? (
            <TablePager
              mode="cursor"
              pageSize={PAGE_SIZE}
              loadedCount={rows.length}
              hasPrev={cursors.length > 1}
              hasNext={page.next !== null}
              onPrev={() => setPaging({ key, cursors: cursors.slice(0, -1) })}
              onNext={() => page.next !== null && setPaging({ key, cursors: [...cursors, page.next] })}
              label={`${rows.length} of ${page.checks.length} on this page${rows.length < page.checks.length ? ' (the browser filters hide the rest)' : ''}`}
            />
          ) : undefined
        }
      >
        <thead className="border-b border-border bg-muted/40 text-xs font-medium text-muted-foreground">
          <tr>
            {['When', 'Person', 'App', 'Access', 'Answer', 'Reason'].map((label) => (
              <th key={label} scope="col" className="px-4 py-2.5 font-medium">{label}</th>
            ))}
          </tr>
        </thead>
        {error ? (
          <DataTableLoadError colSpan={6} message={error} onRetry={() => void load()} />
        ) : !page ? (
          <DataTableMessage colSpan={6} loading>
            Loading the log
          </DataTableMessage>
        ) : rows.length === 0 ? (
          <DataTableMessage colSpan={6}>No check matches these filters.</DataTableMessage>
        ) : (
          <tbody>
            {rows.map((check) => (
              <Fragment key={check.id}>
                <tr className="cursor-pointer border-b border-border hover:bg-muted/40" onClick={() => setOpenRow(openRow === check.id ? undefined : check.id)} aria-expanded={openRow === check.id}>
                  <td className="whitespace-nowrap px-4 py-2.5 text-muted-foreground">{formatDateTime(check.at)}</td>
                  <td className="px-4 py-2.5">{check.subject_label || `${check.subject.type}:${check.subject.id}`}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{appName(check.app)}</td>
                  <td className="px-4 py-2.5">{objectText(`${check.resource.type}:${check.resource.id}`)}</td>
                  <td className="px-4 py-2.5">
                    <StatusPill tone={check.answer === 'allow' ? 'success' : 'danger'}>{check.answer === 'allow' ? 'Allow' : 'Deny'}</StatusPill>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">{check.reason || '–'}</td>
                </tr>
                {openRow === check.id ? (
                  <tr className="border-b border-border bg-muted/20">
                    <td colSpan={6} className="px-4 py-3 text-xs text-muted-foreground">
                      <dl className="grid gap-x-6 gap-y-1 md:grid-cols-2">
                        <div>Check {check.id} from {check.service}</div>
                        <div>Action {check.action}, relation {check.relation}</div>
                        <div>Missing: {check.missing?.length ? check.missing.join(', ') : 'nothing recorded'}</div>
                        <div>Resource {check.resource.type}:{check.resource.id}</div>
                      </dl>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        )}
      </DataTable>
    </div>
  );
}

/** A text box that lists matches. A pick shows as text with a Clear. */
function SearchPick({ label, value, onPick, search, hint }: { label: string; value?: Choice; onPick: (pick?: Choice) => void; search: (query: string) => Promise<Choice[]>; hint?: string }) {
  const [text, setText] = useState('');
  const [options, setOptions] = useState<Choice[]>([]);
  const query = useDebouncedValue(text.trim(), 250);
  useEffect(() => {
    let live = true;
    if (query.length < 1) {
      setOptions([]);
      return undefined;
    }
    search(query).then((found) => live && setOptions(found.slice(0, 6))).catch(() => live && setOptions([]));
    return () => {
      live = false;
    };
  }, [query, search]);
  if (value) {
    return (
      <div className="text-xs text-muted-foreground">
        {label}
        <div className="mt-1 flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm text-foreground">
          {value.label}
          <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => onPick(undefined)}>
            Clear
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="text-xs text-muted-foreground">
      <label>
        {label}
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={`Search ${label.toLowerCase()}`} aria-label={`Search ${label.toLowerCase()}`} className="mt-1" />
      </label>
      {hint ? <p className="mt-1 text-destructive-foreground">{hint}</p> : null}
      {options.length > 0 ? (
        <ul className="mt-1 rounded-md border border-border bg-card">
          {options.map((option) => (
            <li key={option.id}>
              <button type="button" className="w-full px-3 py-1.5 text-left text-sm text-foreground hover:bg-muted/50" onClick={() => { onPick(option); setText(''); }}>
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function FilterPopover({ filters, onApply, onClose }: { filters: LogFilters; onApply: (filters: LogFilters) => void; onClose: () => void }) {
  const [draft, setDraft] = useState(filters);
  const [personHint, setPersonHint] = useState<string>();
  const [orgId, setOrgId] = useState<string>();
  const [levels, setLevels] = useState<Choice[]>([]);

  useEffect(() => {
    adminClient.getTeamAccess().then((access) => setOrgId(pickOrg(access.orgs ?? [], readRememberedOrg()))).catch(() => undefined);
    permissionsApi
      .askable()
      .then((list) => setLevels(list.flatMap((i) => i.levels.map((l) => ({ id: `${i.id}.${l.id}`, label: `${i.id} ${l.id}` })))))
      .catch(() => undefined);
  }, []);

  const searchPeople = useCallback(
    async (q: string) => {
      if (!orgId) return [];
      const out = await adminClient.getTeamPeople({ orgId, q, access: 'everyone', page: 1, pageSize: 6 });
      return out.people.flatMap((p) => (p.email ? [{ id: p.email, label: p.email }] : []));
    },
    [orgId],
  );
  const searchLevels = useCallback(async (q: string) => levels.filter((l) => l.label.includes(q.toLowerCase())), [levels]);

  // The service filters checks by person id. The person's grants carry it.
  const pickPerson = async (pick?: Choice) => {
    setPersonHint(undefined);
    if (!pick) return setDraft({ ...draft, person: undefined });
    try {
      const ref = await permissionsApi.personRef(pick.id);
      if (!ref) return setPersonHint('This person has no grant yet, so the service has no id to filter by.');
      setDraft({ ...draft, person: { id: ref, label: pick.label } });
    } catch (failure) {
      setPersonHint(errorText(failure));
    }
  };

  return (
    <div role="dialog" aria-label="Log filters" className="absolute left-0 z-20 mt-1 w-80 max-w-[calc(100vw-2rem)] space-y-3 rounded-lg border border-border bg-card p-4 shadow-lg" onKeyDown={(e) => e.key === 'Escape' && onClose()}>
      <div>
        <p className="mb-1 text-xs text-muted-foreground">Answer</p>
        <SegmentedFilter<LogFilters['answer']> label="Answer" value={draft.answer} onChange={(answer) => setDraft({ ...draft, answer })} options={[{ id: 'deny', label: 'Deny' }, { id: 'allow', label: 'Allow' }, { id: 'all', label: 'All' }]} />
      </div>
      <SearchPick label="Person" value={draft.person} onPick={(pick) => void pickPerson(pick)} search={searchPeople} hint={personHint} />
      <SearchPick label="Integration" value={draft.level} onPick={(level) => setDraft({ ...draft, level })} search={searchLevels} />
      <label className="block text-xs text-muted-foreground">
        Period
        <select value={draft.period} onChange={(e) => setDraft({ ...draft, period: e.target.value as Period })} className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground">
          {(Object.keys(PERIOD_TEXT) as Period[]).map((p) => (
            <option key={p} value={p}>
              {PERIOD_TEXT[p]}
            </option>
          ))}
        </select>
      </label>
      <p className="text-xs text-muted-foreground">These three filter the loaded page only.</p>
      {(['app', 'reason', 'missing'] as const).map((field) => (
        <label key={field} className="block text-xs capitalize text-muted-foreground">
          {field}
          <Input value={draft[field]} onChange={(e) => setDraft({ ...draft, [field]: e.target.value })} aria-label={`Filter by ${field}`} className="mt-1" />
        </label>
      ))}
      <div className="flex justify-between gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => setDraft(DEFAULT_FILTERS)}>
          Reset
        </Button>
        <Button type="button" size="sm" onClick={() => onApply(draft)}>
          Apply
        </Button>
      </div>
    </div>
  );
}
